/**
 * First-time setup, shown mid-sign-in.
 *
 * Reached when a Super Admin has required two-step sign-in for someone who has
 * not set it up yet. They have already proved their password, so the job here
 * is to get them enrolled and into the system in one pass rather than sending
 * them away to phone IT.
 *
 * The backup codes are the last screen on purpose: they are shown once, and
 * nobody gets past them by accident.
 */
import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Loader2, ShieldCheck, Smartphone, Mail, Copy, Check, Printer, AlertTriangle, ArrowRight,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export const OtpSetupForm = ({ setup, isRTL, startSetup, confirmSetup, skipSetup, onComplete }) => {
  const [stage, setStage] = useState('target'); // target | code | codes
  // When an administrator has decided the channel there is nothing to choose:
  // the server refuses a disagreeing request, so offering buttons would only
  // produce a rejection.
  const [channel, setChannel] = useState(setup.suggestedChannel === 'sms' ? 'sms' : 'email');
  const [mobile, setMobile] = useState('');
  const [code, setCode] = useState('');
  const [maskedTarget, setMaskedTarget] = useState(setup.suggestedMaskedTarget || '');
  const [sentChannel, setSentChannel] = useState(null);
  const [backupCodes, setBackupCodes] = useState([]);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const codeRef = useRef(null);
  // The server already knows a number for this person if it suggested SMS, so
  // they should not have to retype it to choose SMS.
  const hasMobileOnRecord = setup.suggestedChannel === 'sms';

  useEffect(() => {
    if (stage === 'code') codeRef.current?.focus();
  }, [stage]);

  const send = async () => {
    setError('');
    setBusy(true);
    try {
      const result = await startSetup(setup.setupTicket, channel === 'sms' ? mobile : null, channel);      if (result.grace) {
        // The code could not be delivered and there is no second factor on this
        // account yet, so the server signed us in rather than leave us stuck at
        // a gate that cannot open. Say so plainly on the way through.
        toast.warning(
          isRTL
            ? 'تعذّر إرسال الرمز، وتم تسجيل دخولك بكلمة المرور. أبلغ المسؤول لإصلاح الإرسال.'
            : 'The code could not be delivered, so you have been signed in with your password. Two-step sign-in is still required once delivery is fixed.',
          { duration: 12000 }
        );
        onComplete();
        return;
      }
      if (result.success) {
        setMaskedTarget(result.maskedTarget);
        setSentChannel(result.channel);
        setStage('code');
      } else {
        setError(result.error);
      }
    } finally {
      setBusy(false);
    }
  };

  const skip = async () => {
    setError('');
    setBusy(true);
    try {
      const result = await skipSetup(setup.setupTicket);
      if (result.success) {
        toast.warning(
          isRTL
            ? 'تم تسجيل دخولك بكلمة المرور. الدخول بخطوتين مطلوب ولم يُفعّل بعد.'
            : 'Signed in with your password. Two-step sign-in is still required and not set up yet.',
          { duration: 12000 }
        );
        onComplete();
      } else {
        setError(result.error);
      }
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {    setError('');
    setBusy(true);
    try {
      const result = await confirmSetup(setup.setupTicket, code, rememberDevice);
      if (result.success) {
        setBackupCodes(result.backupCodes || []);
        setStage('codes');
      } else {
        setError(result.error);
        setCode('');
      }
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = async () => {
    await navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const primaryButton = cn(
    'w-full h-12 rounded-lg font-semibold text-brand-beige text-base inline-flex items-center justify-center gap-2',
    'bg-brand-black hover:bg-[#2A2624] shadow-brand-md transition-colors',
    'focus:outline-none focus:ring-4 focus:ring-brand-orange/30',
    'disabled:opacity-60 disabled:cursor-not-allowed'
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-6"
      data-testid="otp-setup-form"
    >
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-full bg-brand-orange/15 flex items-center justify-center shrink-0">
          <ShieldCheck className="h-5 w-5 text-brand-orange" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-brand-black tracking-tight">
            {isRTL ? 'إعداد تسجيل الدخول بخطوتين' : 'Set up two-step sign-in'}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {isRTL
              ? 'حسابك يتطلب رمز تحقق إضافي. الإعداد يستغرق دقيقة واحدة.'
              : 'Your account now needs a verification code. This takes a minute, once.'}
          </p>
        </div>
      </div>

      {error && (
        <div
          className="p-3 rounded-lg text-sm font-medium border"
          style={{
            background: 'hsl(18 84% 30% / 0.08)',
            borderColor: 'hsl(18 84% 30% / 0.30)',
            color: 'hsl(18 84% 24%)',
          }}
          data-testid="otp-setup-error"
        >
          {error}
        </div>
      )}

      {stage === 'target' && (
        <div className="space-y-5" data-testid="otp-setup-target-stage">
          {setup.smsAvailable && !setup.channelLocked && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setChannel('sms')}
                className={cn(
                  'flex-1 p-3 rounded-lg border text-sm font-medium transition-colors inline-flex items-center justify-center gap-2',
                  channel === 'sms'
                    ? 'border-brand-orange bg-brand-orange/10 text-brand-black'
                    : 'border-border text-muted-foreground hover:border-brand-orange/50'
                )}
                data-testid="otp-setup-channel-sms"
              >
                <Smartphone className="h-4 w-4" />
                {isRTL ? 'رسالة نصية' : 'Text message'}
              </button>
              <button
                type="button"
                onClick={() => setChannel('email')}
                className={cn(
                  'flex-1 p-3 rounded-lg border text-sm font-medium transition-colors inline-flex items-center justify-center gap-2',
                  channel === 'email'
                    ? 'border-brand-orange bg-brand-orange/10 text-brand-black'
                    : 'border-border text-muted-foreground hover:border-brand-orange/50'
                )}
                data-testid="otp-setup-channel-email"
              >
                <Mail className="h-4 w-4" />
                {isRTL ? 'البريد الإلكتروني' : 'Email'}
              </button>
            </div>
          )}

          {setup.channelLocked && (
            <div
              className="p-3 rounded-lg border border-border bg-muted/30 text-sm flex items-start gap-2"
              data-testid="otp-setup-channel-locked"
            >
              {channel === 'sms'
                ? <Smartphone className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                : <Mail className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />}
              <span className="text-muted-foreground">
                {isRTL
                  ? `حدّد المسؤول أن يصلك الرمز عبر ${channel === 'sms' ? 'رسالة نصية' : 'البريد الإلكتروني'}.`
                  : `Your administrator has set your codes to arrive by ${channel === 'sms' ? 'text message' : 'email'}.`}
              </span>
            </div>
          )}

          {channel === 'sms' ? (
            <div className="space-y-2">
              <Label htmlFor="setup-mobile" className="text-brand-black font-medium">
                {hasMobileOnRecord
                  ? (isRTL
                    ? `سنرسل الرمز إلى ${setup.suggestedMaskedTarget} — اتركه فارغاً لاستخدامه`
                    : `We'll text ${setup.suggestedMaskedTarget} — leave blank to use it`)
                  : (isRTL ? 'رقم جوالك السعودي' : 'Your Saudi mobile number')}
              </Label>
              <Input
                id="setup-mobile"
                value={mobile}
                onChange={(event) => setMobile(event.target.value)}
                placeholder="0548080608"
                className="h-12 bg-card border-border focus:ring-2 focus:ring-brand-orange"
                data-testid="otp-setup-mobile-input"
                dir="ltr"
              />
              <p className="text-xs text-muted-foreground">
                {isRTL
                  ? 'لن يُحفظ الرقم قبل التحقق منه برمز.'
                  : "We won't save the number until a code sent to it comes back."}
              </p>
            </div>
          ) : (
            <div className="p-3 rounded-lg border border-border bg-muted/30 text-sm" data-testid="otp-setup-email-target">
              <span className="text-muted-foreground">
                {isRTL ? 'سيُرسل الرمز إلى ' : 'The code will be emailed to '}
              </span>
              <span className="font-medium" dir="ltr">{setup.suggestedMaskedTarget}</span>
            </div>
          )}

          <motion.button
            type="button"
            onClick={send}
            disabled={busy || (channel === 'sms' && !mobile && !hasMobileOnRecord)}
            whileHover={!busy ? { y: -1 } : {}}
            whileTap={!busy ? { scale: 0.98 } : {}}
            className={primaryButton}
            data-testid="otp-setup-send"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            {isRTL ? 'إرسال الرمز' : 'Send me a code'}
          </motion.button>
        </div>
      )}

      {stage === 'code' && (
        <div className="space-y-5" data-testid="otp-setup-code-stage">
          <div className="space-y-2">
            <Label htmlFor="setup-code" className="text-brand-black font-medium">
              {isRTL
                ? `أدخل الرمز المُرسل إلى ${maskedTarget}`
                : `Enter the code sent to ${maskedTarget}`}
            </Label>
            <Input
              id="setup-code"
              ref={codeRef}
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder="000000"
              maxLength={6}
              inputMode="numeric"
              autoComplete="one-time-code"
              className="h-14 text-2xl tracking-[0.5em] text-center font-semibold bg-card border-border focus:ring-2 focus:ring-brand-orange"
              data-testid="otp-setup-code-input"
              dir="ltr"
            />
            {sentChannel === 'email' && (
              <p className="text-xs text-muted-foreground">
                {isRTL ? 'تحقق من مجلد البريد غير المرغوب إذا لم تجده.' : "Check your spam folder if it isn't there."}
              </p>
            )}
          </div>

          <label className="flex items-center gap-3 text-sm text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={rememberDevice}
              onChange={(event) => setRememberDevice(event.target.checked)}
              className="h-4 w-4 rounded border-border accent-[hsl(28_65%_47%)]"
              data-testid="otp-setup-remember-device"
            />
            {isRTL ? 'لا تسألني على هذا المتصفح لمدة ٧ أيام' : "Don't ask on this browser for 7 days"}
          </label>

          <motion.button
            type="button"
            onClick={confirm}
            disabled={busy || code.length < 6}
            whileHover={!busy ? { y: -1 } : {}}
            whileTap={!busy ? { scale: 0.98 } : {}}
            className={primaryButton}
            data-testid="otp-setup-confirm"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {isRTL ? 'تأكيد وإكمال' : 'Confirm and finish'}
          </motion.button>

          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => { setStage('target'); setCode(''); setError(''); }}
              className="text-sm text-muted-foreground hover:text-brand-black transition-colors self-start"
              data-testid="otp-setup-back"
            >
              {isRTL ? 'تغيير الوجهة' : 'Change where it goes'}
            </button>

            {setup.graceAllowed && (
              <button
                type="button"
                onClick={skip}
                disabled={busy}
                className="text-sm text-brand-brown hover:text-brand-orange transition-colors self-start"
                data-testid="otp-setup-skip"
              >
                {isRTL
                  ? 'لم يصلني الرمز — سجّل دخولي بكلمة المرور'
                  : "The code never arrived — sign me in with my password"}
              </button>
            )}
          </div>
        </div>
      )}

      {stage === 'codes' && (
        <div className="space-y-4" data-testid="otp-setup-backup-codes">
          <div
            className="p-3 rounded-lg text-sm border flex items-start gap-2"
            style={{
              background: 'hsl(38 92% 40% / 0.10)',
              borderColor: 'hsl(38 92% 40% / 0.32)',
              color: 'hsl(30 70% 24%)',
            }}
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              {isRTL
                ? 'هذه الرموز تُعرض مرة واحدة فقط. اطبعها أو احفظها الآن — كل رمز يعمل مرة واحدة، وتُستخدم إذا فُقد هاتفك أو تعذّر وصول الرمز.'
                : 'Shown once only. Print or save these now — each works a single time, and they are how you get in if your phone is lost or a code never arrives.'}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 font-mono text-sm" dir="ltr">
            {backupCodes.map((backupCode, index) => (
              <div
                key={backupCode}
                className="px-3 py-2 rounded border bg-muted/40"
                data-testid={`otp-setup-backup-code-${index}`}
              >
                {backupCode}
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={copyCodes}
              className="px-3 py-2 rounded-lg border border-border text-sm font-medium inline-flex items-center gap-2 hover:border-brand-orange transition-colors"
              data-testid="otp-setup-copy-codes"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? (isRTL ? 'تم النسخ' : 'Copied') : (isRTL ? 'نسخ' : 'Copy')}
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              className="px-3 py-2 rounded-lg border border-border text-sm font-medium inline-flex items-center gap-2 hover:border-brand-orange transition-colors"
              data-testid="otp-setup-print-codes"
            >
              <Printer className="h-4 w-4" />
              {isRTL ? 'طباعة' : 'Print'}
            </button>
          </div>

          <motion.button
            type="button"
            onClick={onComplete}
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.98 }}
            className={primaryButton}
            data-testid="otp-setup-done"
          >
            {isRTL ? 'حفظتها — متابعة' : "I've saved them — continue"}
          </motion.button>
        </div>
      )}
    </motion.div>
  );
};

export default OtpSetupForm;
